import { Component, computed, effect, inject, input, output } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { FormControl, Validators } from '@angular/forms'
import { WorkspaceRoleService } from '@app/modules/auth/services/workspace-role.service'
import {
  PERSONA_ARCHIVE_MENU_KEY,
  WIZARD_MOBILE_PERSONA_MENU_DROPDOWN_HEIGHTS,
  getPersonaMenuDropdownHeight,
  withoutPersonaArchiveMenuOption
} from '@app/v2/event/components/event-personas/event-persona-menu-options.constants'
import { DropdownComponent } from '@app/v2/shared/components/dropdown/dropdown.component'
import { DropdownOption } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { BasicInputComponent } from '@app/v2/shared/components/inputs/basic-input/basic-input.component'
import { MobilePersonaCardComponent } from '@app/v2/shared/components/mobile-persona-card/mobile-persona-card.component'
import { ScaleBadgeDirective } from '@app/v2/shared/directives/scale-badge.directive'
import { BasicInputMode } from '@app/v2/shared/enums/input-modes.enum'
import { SafeDomPipe } from '@app/v2/shared/pipes/safe-dom.pipe'
import { Persona } from '../types/persona.type'

@Component({
  selector: 'app-persona-card',
  imports: [BasicInputComponent, DropdownComponent, MobilePersonaCardComponent, SafeDomPipe, ScaleBadgeDirective],
  templateUrl: './persona-card.component.html',
  styleUrl: './persona-card.component.scss'
})
export class PersonaCardComponent {
  public persona = input.required<Persona>()
  public readonly personaNameCommit = output<Persona>()
  public readonly landingPageClick = output<Persona>()
  public readonly BasicInputMode = BasicInputMode

  public readonly personaNameControl = new FormControl('', { nonNullable: true, validators: [Validators.required] })
  public clonePersona = output<Persona>()
  public archivePersona = output<Persona>()
  public readonly managePersona = output<Persona>()

  protected readonly personaMenuOptions = computed(() => {
    this._userRole()
    return this._workspaceRoleService.canArchiveItems() ? PersonaCardComponent._personaMenuOptions : withoutPersonaArchiveMenuOption(PersonaCardComponent._personaMenuOptions)
  })

  protected readonly personaMenuDropdownHeight = computed(() => (this.personaMenuOptions().some((option) => option.key === PERSONA_ARCHIVE_MENU_KEY) ? 100 : 51))
  protected readonly personaMobileMenuDropdownHeight = computed(() => getPersonaMenuDropdownHeight(this.personaMenuOptions(), WIZARD_MOBILE_PERSONA_MENU_DROPDOWN_HEIGHTS))

  private static readonly _personaMenuOptions: DropdownOption[] = [
    {
      key: 'clone',
      value: 'Clone persona',
      prefixIcon: `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.5422 6.70971e-06H8.05033C7.78761 -0.000684373 7.52736 0.0520158 7.28465 0.155056C7.04194 0.258097 6.8216 0.409432 6.63637 0.600299L6.16504 1.08176C6.09114 1.16196 6.03758 1.25951 6.00905 1.36586H8.87565C9.57682 1.37253 10.2468 1.66392 10.7385 2.17606C11.2301 2.6882 11.5034 3.37926 11.4982 4.09757V10.9268H11.5422C11.8899 10.9204 12.2208 10.773 12.4625 10.5169C12.7042 10.2609 12.8369 9.91711 12.8315 9.56098V1.36586C12.8369 1.00973 12.7042 0.665964 12.4625 0.409925C12.2208 0.153886 11.8899 0.00647319 11.5422 6.70971e-06Z" fill="#1C64F2"/><path d="M4.83174 6.14633V2.7317C4.83262 2.63217 4.85541 2.53413 4.8984 2.44487C4.4573 2.53916 4.05272 2.76348 3.73443 3.09023L1.84848 5.02223C1.54789 5.33217 1.33678 5.72119 1.23849 6.14633H4.83174Z" fill="#1C64F2"/><path d="M8.87564 2.39024H6.06304C6.1267 2.49246 6.16196 2.61051 6.16504 2.73171V6.14634C6.16504 6.50859 6.02457 6.856 5.77453 7.11215C5.52448 7.36829 5.18535 7.5122 4.83174 7.5122H1.16516V12.2927C1.15982 12.7393 1.32757 13.1698 1.63162 13.4898C1.93567 13.8099 2.35121 13.9933 2.78712 14H8.87564C9.31161 13.9933 9.72722 13.8099 10.0314 13.4899C10.3355 13.1698 10.5034 12.7393 10.4983 12.2927V4.09756C10.5034 3.65093 10.3355 3.22042 10.0314 2.90038C9.72722 2.58035 9.31161 2.3969 8.87564 2.39024Z" fill="#1C64F2"/></svg>`
    },
    {
      key: 'archive',
      value: 'Archive',
      textColorClass: 'tw-text-red-600',
      prefixIcon: `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.2222 0.582031H0.777778C0.348223 0.582031 0 0.920012 0 1.33693V2.84674C0 3.26366 0.348223 3.60164 0.777778 3.60164H13.2222C13.6518 3.60164 14 3.26366 14 2.84674V1.33693C14 0.920012 13.6518 0.582031 13.2222 0.582031Z" fill="#E02424"/><path d="M0.777778 5.11144V11.9056C0.777778 12.306 0.941666 12.69 1.23339 12.9732C1.52511 13.2563 1.92077 13.4154 2.33333 13.4154H11.6667C12.0792 13.4154 12.4749 13.2563 12.7666 12.9732C13.0583 12.69 13.2222 12.306 13.2222 11.9056V5.11144H0.777778ZM9.33333 7.37615C9.33333 7.57636 9.25139 7.76837 9.10553 7.90995C8.95967 8.05152 8.76184 8.13105 8.55556 8.13105H5.44444C5.23817 8.13105 5.04033 8.05152 4.89447 7.90995C4.74861 7.76837 4.66667 7.57636 4.66667 7.37615V6.62125C4.66667 6.42103 4.74861 6.22902 4.89447 6.08745C5.04033 5.94588 5.23817 5.86635 5.44444 5.86635C5.65072 5.86635 5.84855 5.94588 5.99442 6.08745C6.14028 6.22902 6.22222 6.42103 6.22222 6.62125H7.77778C7.77778 6.42103 7.85972 6.22902 8.00558 6.08745C8.15145 5.94588 8.34928 5.86635 8.55556 5.86635C8.76184 5.86635 8.95967 5.94588 9.10553 6.08745C9.25139 6.22902 9.33333 6.42103 9.33333 6.62125V7.37615Z" fill="#E02424"/></svg>`
    }
  ]

  private readonly _workspaceRoleService = inject(WorkspaceRoleService)
  private readonly _userRole = toSignal(this._workspaceRoleService.getUserRole(), { initialValue: null })
  private _lastSyncedPersonaRef: Persona | null = null

  private _blurSaveSchedule: {
    timeoutId: ReturnType<typeof setTimeout>
    pending: { personaId: number; name: string }
  } | null = null

  private readonly _blurSaveDelayMs = 150

  constructor() {
    effect(() => {
      if (this.persona() !== this._lastSyncedPersonaRef) {
        this._lastSyncedPersonaRef = this.persona()
        this._clearBlurSaveSchedule()
        this.personaNameControl.setValue(this.persona().name ?? '', { emitEvent: false })
      }
    })
  }

  /**
   * True when the field differs from the saved persona name (trimmed), including invalid edits — matches unsaved-close warning.
   */
  public hasUnsavedChanges(): boolean {
    return this._trimmedControl() !== this._trimmedSaved()
  }

  /**
   * Valid, non-empty name that differs from saved; used when saving from the unsaved-changes modal (skips invalid like event-identity).
   * @returns {Persona | null} - The persona with the unsaved changes
   */
  public getUnsavedChanges(): Persona | null {
    const trimmedNow = this._trimmedControl()
    if (trimmedNow === this._trimmedSaved()) return null
    if (trimmedNow === '' || this.personaNameControl.invalid) return null
    return { id: this.persona().id, name: trimmedNow }
  }

  /**
   * Clears debounced blur save without side effects that block the next scheduled save.
   */
  public cancelPendingBlurSave(): void {
    this._clearBlurSaveSchedule()
  }

  /** Clears debounced blur save without side effects that block the next scheduled save. */
  private _clearBlurSaveSchedule(): void {
    if (this._blurSaveSchedule === null) return
    clearTimeout(this._blurSaveSchedule.timeoutId)
    this._blurSaveSchedule = null
  }

  /**
   * Reverts the persona name to the saved value (clears debounced blur save).
   */
  public revertToSaved(): void {
    this.cancelPendingBlurSave()
    this.personaNameControl.setValue(this.persona().name ?? '', { emitEvent: false })
  }

  /**
   * Blur handler aligned with event-identity `handleTextFieldBlur('name')`: empty or whitespace-only reverts to the saved name; unchanged value does not schedule save.
   */
  public handlePersonaNameBlur(): void {
    const trimmedValue = String(this.personaNameControl.value ?? '').trim()

    if (!trimmedValue) {
      this.personaNameControl.setValue(this.persona().name ?? '', { emitEvent: false })
      return
    }

    this.personaNameControl.markAsTouched()
    if (this.personaNameControl.invalid) return
    if (trimmedValue === this._trimmedSaved()) return

    this._scheduleBlurSave(trimmedValue)
  }

  /**
   * Enter blurs the input so the same blur + debounced save path runs once (same as event-identity `handleTextFieldKeydown`).
   * @param event { KeyboardEvent } - The keyboard event
   */
  public handlePersonaNameKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') return
    event.preventDefault()
    ;(event.target as HTMLElement)?.blur()
  }

  /**
   * Trims the control value.
   * @returns {string} - The trimmed control value
   */
  private _trimmedControl(): string {
    return String(this.personaNameControl.value ?? '').trim()
  }

  /**
   * Trims the saved persona name.
   * @returns {string} - The trimmed saved persona name
   */
  private _trimmedSaved(): string {
    return String(this.persona().name ?? '').trim()
  }

  /**
   * Schedules a blur save for the persona name.
   * @param name { string } - The name to save
   */
  private _scheduleBlurSave(name: string): void {
    this._clearBlurSaveSchedule()
    const pending = { personaId: this.persona().id, name }
    const timeoutId = setTimeout(() => {
      this._blurSaveSchedule = null

      if (pending.personaId !== this.persona().id) return

      if (this._trimmedControl() !== pending.name.trim() || this.personaNameControl.invalid) return

      this.personaNameCommit.emit({ id: pending.personaId, name: this._trimmedControl() })
    }, this._blurSaveDelayMs)
    this._blurSaveSchedule = { timeoutId, pending }
  }

  /**
   * Handles the persona menu option click.
   * @param option { DropdownOption } - The dropdown option
   */
  public onPersonaMenuOption(option: DropdownOption): void {
    if (option.key === 'clone') this.clonePersona.emit(this.persona())
    else if (option.key === 'archive') this.archivePersona.emit(this.persona())
  }
  /*
   * Emits the landing page click event.
   * @param event { Event } - The event
   * @returns { void } - The void return type
   */
  public emitLandingPageClick(event?: Event): void {
    event?.stopPropagation()
    if (!this.persona()) return
    this.landingPageClick.emit(this.persona())
  }

  /**
   * Emits the manage persona event.
   * @param event { Event } - The event
   * @returns { void } - The void return type
   */
  public emitManagePersona(event?: Event): void {
    event?.stopPropagation()
    this.managePersona.emit(this.persona())
  }
}
